import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'

export const TeacherClassrooms: React.FC = () => {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [loading, setLoading] = useState(true)

  // Add Assignment / Create Subject Form states
  const [selectedSubId, setSelectedSubId] = useState('')
  const [selectedGrade, setSelectedGrade] = useState('')
  const [selectedRooms, setSelectedRooms] = useState<string[]>([])
  const [isAddingNewSub, setIsAddingNewSub] = useState(false)
  const [newSubCode, setNewSubCode] = useState('')
  const [newSubName, setNewSubName] = useState('')
  const [newSubMaxPre, setNewSubMaxPre] = useState(10)
  const [newSubMaxPost, setNewSubMaxPost] = useState(10)
  const [subjectSearch, setSubjectSearch] = useState('')

  const loadData = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [allAssignments, allSubjects, allClassrooms] = await Promise.all([
        dataService.getTeachingAssignments(),
        dataService.getSubjects(),
        dataService.getClassrooms()
      ])
      setAssignments(allAssignments.filter((a: any) => a.teacher_id === user.id))
      setSubjects(allSubjects)
      setClassrooms(allClassrooms)
    } catch (err) {
      console.error('Error loading taught classrooms:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [user])

  const handleAddClassroom = async (e: React.FormEvent) => {
    e.preventDefault()
    setNotification(null)
    try {
      let subjectId = Number(selectedSubId)

      if (isAddingNewSub) {
        if (!newSubCode || !newSubName) {
          throw new Error('กรุณากรอกรหัสและชื่อวิชาเรียนใหม่ให้ครบถ้วน')
        }
        const newSub = await dataService.createSubject(newSubCode, newSubName, user.department_id || 1, newSubMaxPre, newSubMaxPost)
        subjectId = newSub.id
      }

      if (!subjectId || !selectedGrade || selectedRooms.length === 0) {
        throw new Error('กรุณาเลือกวิชา ระดับชั้น และอย่างน้อยหนึ่งห้องเรียนที่ต้องการลงตารางสอน')
      }

      const newAssignmentsToAdd = []
      for (const roomNum of selectedRooms) {
        let targetClassroom = classrooms.find(c => c.grade_level === Number(selectedGrade) && c.room_number === roomNum)
        if (!targetClassroom) {
          targetClassroom = await dataService.createClassroom(Number(selectedGrade), roomNum)
          const updated = await dataService.getClassrooms()
          setClassrooms(updated)
        }

        const exists = assignments.some(a => a.subject_id === subjectId && a.classroom_id === targetClassroom!.id)
        if (!exists) {
          newAssignmentsToAdd.push(targetClassroom.id)
        }
      }

      if (newAssignmentsToAdd.length === 0) {
        throw new Error('รายวิชานี้ในห้องเรียนที่เลือกทั้งหมด ถูกลงทะเบียนไว้เรียบร้อยแล้ว')
      }

      await Promise.all(
        newAssignmentsToAdd.map(classId => dataService.addAssignment(user.id, subjectId, classId))
      )
      
      // Reset
      setSelectedSubId('')
      setSelectedGrade('')
      setSelectedRooms([])
      setNewSubCode('')
      setNewSubName('')
      setIsAddingNewSub(false)
      setNotification({ type: 'success', message: `➕ ลงทะเบียนวิชาสอนเพิ่มสำเร็จจำนวน ${newAssignmentsToAdd.length} ห้องเรียน` })
      loadData()
    } catch (err: any) {
      setNotification({ type: 'error', message: err.message || 'เกิดข้อผิดพลาดในการลงทะเบียน' })
    }
  }

  const handleDeleteAssignment = async (id: number) => {
    if (window.confirm('ยืนยันลบห้องสอนนี้ออกจากรายการตารางสอนของท่านหรือไม่?')) {
      await dataService.deleteAssignment(id)
      setNotification({ type: 'success', message: '🗑️ ลบห้องสอนออกจากตารางสำเร็จ' })
      loadData()
    }
  }

  const filteredSubjects = subjects.filter(s => 
    s.code.toLowerCase().includes(subjectSearch.toLowerCase()) || 
    s.name.toLowerCase().includes(subjectSearch.toLowerCase())
  )

  const getSubjectName = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : '—'
  }

  const getClassroomStr = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : '—'
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>🏫 จัดการตารางเรียนสอน</h1>
        <p style={{ color: 'var(--text-secondary)' }}>ลงทะเบียนวิชาที่รับผิดชอบและเพิ่มรายวิชา/ห้องเรียนที่ทำการสอนในภาคเรียนนี้</p>
      </div>

      {notification && (
        <div style={{
          background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${notification.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
          borderRadius: '8px',
          padding: '16px',
          color: notification.type === 'success' ? 'var(--success)' : 'var(--danger)',
          fontSize: '14px'
        }}>
          {notification.message}
        </div>
      )}

      <div className="grid-responsive-dashboard">
        {/* Table of classrooms */}
        <div className="glassmorphism" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>ห้องเรียนสอนที่เปิดดำเนินการ</h3>
          {assignments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
              📭 คุณครูยังไม่ได้ระบุห้องเรียนที่ทำการสอนในสาระการเรียนรู้นี้
            </div>
          ) : (
            <div className="custom-table-container">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>วิชาเรียน</th>
                    <th>ห้องสอน</th>
                    <th style={{ textAlign: 'center' }}>การจัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assign) => (
                    <tr key={assign.id}>
                      <td><strong>{getSubjectName(assign.subject_id)}</strong></td>
                      <td><span style={{ fontWeight: '600' }}>{getClassroomStr(assign.classroom_id)}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <button 
                          onClick={() => handleDeleteAssignment(assign.id)} 
                          className="btn btn-outline" 
                          style={{ padding: '4px 8px', fontSize: '12px', color: 'var(--danger)', borderColor: 'rgba(239,68,68,0.15)' }}
                        >
                          🗑️ ลบออกจากตารางสอน
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form add */}
        <div className="glassmorphism" style={{ padding: '28px', alignSelf: 'start' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>➕ ลงทะเบียนเพิ่มห้องเรียนสอน</h3>
          <form onSubmit={handleAddClassroom} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input 
                type="checkbox" 
                id="newSubjectCheckboxClassrooms"
                checked={isAddingNewSub} 
                onChange={(e) => setIsAddingNewSub(e.target.checked)} 
                style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
              />
              <label htmlFor="newSubjectCheckboxClassrooms" style={{ fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                ต้องการสร้างวิชาเรียนใหม่ที่ไม่มีในระบบ
              </label>
            </div>

            {isAddingNewSub ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
                  <div>
                    <label className="input-label" htmlFor="newSubCodeClass">รหัสวิชา</label>
                    <input id="newSubCodeClass" type="text" placeholder="เช่น ว21101" className="input-field" value={newSubCode} onChange={(e) => setNewSubCode(e.target.value)} required />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="newSubNameClass">ชื่อวิชาเรียน</label>
                    <input id="newSubNameClass" type="text" placeholder="เช่น วิทยาศาสตร์พื้นฐาน" className="input-field" value={newSubName} onChange={(e) => setNewSubName(e.target.value)} required />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="input-label" htmlFor="newSubMaxPreClass">เต็ม Pre</label>
                    <input id="newSubMaxPreClass" type="number" className="input-field" value={newSubMaxPre} onChange={(e) => setNewSubMaxPre(Number(e.target.value))} required />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="newSubMaxPostClass">เต็ม Post</label>
                    <input id="newSubMaxPostClass" type="number" className="input-field" value={newSubMaxPost} onChange={(e) => setNewSubMaxPost(Number(e.target.value))} required />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="input-label" htmlFor="subjectSelectClassrooms">เลือกวิชาเรียนของระบบ</label>
                <input 
                  type="text" 
                  placeholder="🔍 ค้นหารหัสวิชา หรือชื่อวิชา..." 
                  className="input-field" 
                  value={subjectSearch} 
                  onChange={(e) => setSubjectSearch(e.target.value)} 
                  style={{ marginBottom: '8px', fontSize: '13px', background: 'var(--bg-input)' }} 
                />
                <select 
                  id="subjectSelectClassrooms"
                  className="input-field" 
                  value={selectedSubId} 
                  onChange={(e) => setSelectedSubId(e.target.value)}
                  style={{ background: 'var(--bg-input)' }}
                >
                  <option value="">-- เลือกวิชาเรียน ({filteredSubjects.length} วิชา) --</option>
                  {filteredSubjects.map(s => (
                    <option key={s.id} value={s.id}>[{s.code}] {s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="input-label" htmlFor="gradeSelectClassrooms">ระดับชั้นเรียน</label>
              <select 
                id="gradeSelectClassrooms"
                className="input-field" 
                value={selectedGrade} 
                onChange={(e) => { setSelectedGrade(e.target.value); setSelectedRooms([]); }}
                style={{ background: 'var(--bg-input)', marginBottom: '12px' }}
                required
              >
                <option value="">-- เลือกชั้นเรียน --</option>
                <option value="7">ม.1</option>
                <option value="8">ม.2</option>
                <option value="9">ม.3</option>
                <option value="10">ม.4</option>
                <option value="11">ม.5</option>
                <option value="12">ม.6</option>
              </select>

              {selectedGrade && (
                <div style={{ marginTop: '4px' }}>
                  <label className="input-label" style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span>เลือกห้องเรียน (ติ๊กได้หลายห้อง)</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        type="button" 
                        onClick={() => setSelectedRooms(Array.from({ length: 26 }, (_, i) => String(i + 1)))} 
                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                      >
                        [เลือกทั้งหมด]
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setSelectedRooms([])} 
                        style={{ background: 'transparent', border: 'none', color: 'var(--danger)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                      >
                        [ล้างทั้งหมด]
                      </button>
                    </div>
                  </label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))', 
                    gap: '8px', 
                    maxHeight: '130px', 
                    overflowY: 'auto', 
                    border: '1px solid var(--border)', 
                    borderRadius: '8px', 
                    padding: '10px', 
                    background: 'var(--bg-input)' 
                  }}>
                    {Array.from({ length: 26 }, (_, i) => String(i + 1)).map(room => {
                      const isChecked = selectedRooms.includes(room)
                      return (
                        <label key={room} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                          <input 
                            type="checkbox" 
                            checked={isChecked} 
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRooms([...selectedRooms, room])
                              } else {
                                setSelectedRooms(selectedRooms.filter(r => r !== room))
                              }
                            }} 
                          />
                          ห้อง {room}
                        </label>
                      )
                    })}
                  </div>
                  <div style={{ fontSize: '11px', marginTop: '6px', color: 'var(--text-secondary)' }}>
                    👉 เลือกแล้ว: {selectedRooms.length} ห้องเรียน
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: '8px' }}>
              ✓ บันทึกห้องเรียนสอน
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
export default TeacherClassrooms
